<script setup lang="ts">
  import { computed } from 'vue';
  import { ColoredButton, IconImage } from '@/shared/ui';
  import type { CurriculumUploadStatus } from '../../model/useCurriculumUploadRow';

  import UploadIcon from '@/shared/assets/icons/UploadIcon.svg';
  import PdfFileIcon from '@/shared/assets/icons/PdfFileIcon.svg';
  import UploadSuccessIcon from '@/shared/assets/icons/UploadSuccessIcon.svg';
  import GrayTrashIcon from '@/shared/assets/icons/GrayTrashIcon.svg';

  const props = defineProps<{
    testid: string;
    curriculumFileName: string | null;
    uploadProgress: number;
    uploadStatus: CurriculumUploadStatus;
  }>();

  const emit = defineEmits<{
    (e: 'select-file', file: File): void;
    (e: 'remove-file'): void;
  }>();

  const isUploading = computed(() => props.uploadStatus === 'uploading');

  const openFilePicker = (event: MouseEvent): void => {
    const button = event.currentTarget as HTMLElement;
    button.parentElement?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  };

  const handleFileChange = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) emit('select-file', file);
    input.value = '';
  };
</script>

<template>
  <div
    v-if="!curriculumFileName"
    :data-testid="`${testid}-upload-button`"
  >
    <ColoredButton
      text="Upload"
      :icon="UploadIcon"
      icon-alt="Upload de currículo"
      @click="openFilePicker"
    />
    <input
      :data-testid="`${testid}-file-input`"
      type="file"
      accept="application/pdf"
      class="hidden"
      @change="handleFileChange"
    >
  </div>

  <div
    v-else
    :data-testid="`${testid}-file-row`"
    class="flex items-center gap-6"
  >
    <div class="flex min-w-0 flex-1 items-center gap-6">
      <div class="flex shrink-0 items-center gap-2">
        <IconImage
          :src="PdfFileIcon"
          alt="Arquivo PDF"
          class="h-6 w-6 shrink-0"
        />
        <span class="truncate text-sm text-gray-07">{{ curriculumFileName }}</span>
      </div>
      <div class="flex min-w-0 flex-1 items-end gap-2">
        <div class="flex min-w-0 flex-1 items-center justify-end gap-3">
          <div class="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-02">
            <div
              :data-testid="`${testid}-progress-bar`"
              class="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
              :class="uploadProgress === 100 ? 'bg-green-19' : 'bg-[var(--theme-secondary)]'"
              :style="{ width: `${uploadProgress}%` }"
            />
          </div>
          <span
            :data-testid="`${testid}-progress-label`"
            class="min-w-[44px] text-right text-sm text-gray-07"
          >{{ uploadProgress }}%</span>
        </div>
        <IconImage
          v-if="uploadStatus === 'completed'"
          :data-testid="`${testid}-upload-success`"
          :src="UploadSuccessIcon"
          alt="Upload concluído"
          class="h-6 w-6 shrink-0"
        />
      </div>
    </div>
    <button
      v-if="!isUploading"
      type="button"
      :data-testid="`${testid}-remove-file`"
      aria-label="Remover currículo"
      class="text-gray-03"
      @click="emit('remove-file')"
    >
      <IconImage
        :src="GrayTrashIcon"
        alt="Remover currículo"
        class="h-6 w-6 cursor-pointer"
      />
    </button>
  </div>
</template>
